// Copyright 2021 Prescryptive Health, Inc.
import { ReactElement, useState, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CssBaseline from '@mui/material/CssBaseline';
import { usePostAuthContext } from '../../../providers/post-auth/use-post-auth-context.hook';
import { useProviderContext } from '../../../providers/provider/use-provider-context.hook';
import { useLocationContext } from '../../../providers/location/use-location-context.hook';
import { useAppointmentsContext } from '../../../providers/appointments/use-appointments-context.hook';
import { appointmentsClearDispatch } from '../../../state/appointments/dispatch/appointments-clear.dispatch';
import { locationClearDispatch } from '../../../state/location/dispatch/location-clear.dispatch';
import { SecondaryHeader } from '../../../components/secondary-header/secondary-header';
import PostAuthDrawer from './drawer-v2/post-auth-drawer';
import { AppointmentsContextProvider } from '../../../providers/appointments/appointments.context-provider';
import { topBarHeight } from './styles';
import { authenticatedRouteConfig } from '../../authenticated-route-config';
import { Box, Typography } from '@mui/material';
import { Route, Routes, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { callAfterIdle } from '../../../helpers/callAfterIdle/callAfterIdle';
import { Main } from './post-auth.drawer-navigator.styled-components';
import { useSessionContext } from '../../../providers/session/use-session-context.hook';
import { TermsOfUseModal } from '../../../components/modals/terms-of-use/terms-of-use.modal';
import { useAuth0 } from '@auth0/auth0-react';
import { Banner } from '../../../components/banner/banner';
import { LoginWithAuth0Modal } from '../../../components/modals/login-with-auth0/login-with-auth0.modal';
import { Auth0IdentityCreationContainer } from '../../../components/modals/auth0-identity-creation-modals/auth0-identity-creation.container';
import { localStorageKeys } from '../../../constants/local-storage-keys';
import { DrugSearchScreen } from '../../../components/screens/drug-search/drug-search.screen';
import { DrugSearchConfigContextProvider } from '../../../providers/drug-search-config/drug-search-config.context-provider';

export const PostAuthDrawerNavigator = (): ReactElement => {
  const { pathname } = useLocation();
  const { user } = useAuth0();
  const navigate = useNavigate();
  const {
    authState: { userAccess },
  } = usePostAuthContext();

  const {
    providerState: { currentProvider },
  } = useProviderContext();

  const {
    sessionState: { authProvider },
  } = useSessionContext();

  const { auth0Migration, auth0MigrationBanner, autoSignOut } = useFlags();

  const isBannerOpen = auth0MigrationBanner && isAADUser(user?.sub, authProvider?.isLoggedIn());

  const currentProviderRules = userAccess?.rules.find(
    (rule) => rule.name === currentProvider?.name
  );

  const canAccessAdminScreens = currentProviderRules && currentProviderRules.role === 'Admin';

  const canAccessUserScreens =
    currentProviderRules &&
    (currentProviderRules.role === 'User' || currentProviderRules.role === 'Admin');

  const [isTermsOfUseDialogOpen, setIsTermsOfUseModalOpen] = useState<boolean>(true);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(
    auth0Migration
      ? user?.[`${process.env.REACT_APP_AUTH0_NAMESPACE}/userMetadata`]?.pharmacyPortalService
          ?.termsOfUseAccepted ||
          JSON.parse(localStorage.getItem(user?.sub as string) as string) ||
          false
      : true
  );

  const handleCloseTermsOfUseDialog = () => setIsTermsOfUseModalOpen(false);

  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const closeDrawer = () => {
    setMobileOpen(false);
  };

  const { locationDispatch } = useLocationContext();
  const { appointmentsDispatch } = useAppointmentsContext();

  useEffect(() => {
    appointmentsClearDispatch(appointmentsDispatch);
    locationClearDispatch(locationDispatch);
  }, [currentProvider?.name]);

  const currentAccount = authProvider?.getAccount();
  useEffect(() => {
    if (user?.email) {
      localStorage.setItem(localStorageKeys.loginHint, user.email);
      return;
    }

    if (currentAccount) {
      localStorage.setItem(localStorageKeys.loginHint, currentAccount.userName);
    }
  }, [user?.email, currentAccount]);

  useEffect(() => {
    if (termsAccepted) {
      setIsTermsOfUseModalOpen(false);
    }

    if (
      user?.[`${process.env.REACT_APP_AUTH0_NAMESPACE}/userMetadata`]?.pharmacyPortalService
        ?.termsOfUseAccepted
    ) {
      localStorage.removeItem(user?.sub as string);
    }
  }, [termsAccepted]);

  useEffect(() => {
    if (!authProvider?.logout) return;
    if (autoSignOut) {
      const idleTime = process.env.REACT_APP_IDLE_LOGOUT_TIME as unknown as number;
      callAfterIdle(idleTime, authProvider.logout);
    }
  }, []);

  const routesWithoutDrawerRegex = new RegExp('(/appointments/.*)|(/blocked-times/.*)');

  const isCurrentPageOnRouteWithoutDrawer = routesWithoutDrawerRegex.test(pathname);
  const mainOverrideStyle = isCurrentPageOnRouteWithoutDrawer
    ? {
        marginLeft: 0,
        maxWidth: '100%',
        height: `calc(100vh - ${topBarHeight}px)`,
      }
    : {};

  const onBackButtonPress = () => {
    void navigate(authenticatedRouteConfig.appointments.createUrl());
  };

  return (
    <Box display='flex' flexDirection='column' width='1'>
      <CssBaseline />
      {isCurrentPageOnRouteWithoutDrawer && (
        <SecondaryHeader onBackButtonPress={onBackButtonPress} />
      )}
      {!isCurrentPageOnRouteWithoutDrawer && (
        <PostAuthDrawer
          isMobileOpen={mobileOpen}
          onHandleDrawerToggle={handleDrawerToggle}
          closeDrawer={closeDrawer}
          canAccessAdminScreens={!!canAccessAdminScreens}
          canAccessUserScreens={!!canAccessUserScreens}
        />
      )}
      <Main style={mainOverrideStyle}>
        {isBannerOpen && currentProvider && <Banner />}
        {!currentProvider ? (
          <Box display='flex' justifyContent='center' alignItems='center' height='inherit'>
            {/* useTranslation hook breaks the router for some reason and serves a blank screen on refresh. We will use a hard coded string for now */}
            <Typography>{'Please select a provider'}</Typography>
          </Box>
        ) : (
          <AppointmentsContextProvider>
            <Routes>
              <Route
                path={authenticatedRouteConfig.drugSearch.route}
                element={
                  <DrugSearchConfigContextProvider>
                    <DrugSearchScreen />
                  </DrugSearchConfigContextProvider>
                }
              />
              <Route
                path='*'
                element={<Navigate to={authenticatedRouteConfig.drugSearch.route} replace />}
              />
            </Routes>
          </AppointmentsContextProvider>
        )}
      </Main>
      {auth0Migration && <Auth0IdentityCreationContainer />}
      {auth0Migration && (
        <TermsOfUseModal
          isOpen={isTermsOfUseDialogOpen}
          handleClose={handleCloseTermsOfUseDialog}
          setTermsAccepted={setTermsAccepted}
        />
      )}
      {auth0Migration && <LoginWithAuth0Modal />}
    </Box>
  );
};

function isAADUser(sub: string | undefined, isLoggedInAAD: boolean | undefined): boolean {
  if (isLoggedInAAD) return true;
  if (sub === undefined) return false;
  return 'waad' === sub.slice(0, 4);
}
